import { COOKIE_KEYS } from 'constants/common'
import {
  getPermissionFromToken,
  getUserPermissions,
} from 'hooks/useThriveAuth/helpers'

export const OSANO_CMP_SCRIPT_SRC =
  process.env.NEXT_PUBLIC_OSANO_CMP_SCRIPT_SRC || ''

export const isOsanoCmpEnabled = Boolean(OSANO_CMP_SCRIPT_SRC)

/**
 * Resolve the Osano CMP script URL for a request/user.
 * Returns "" for clinicians and support users so callers can omit the script.
 */
export function resolveOsanoCmpScriptSrc(accessToken?: string | null): string {
  if (!OSANO_CMP_SCRIPT_SRC) {
    return ''
  }

  if (!accessToken) {
    return OSANO_CMP_SCRIPT_SRC
  }

  const permissions = getPermissionFromToken(accessToken) ?? []
  const { isSupportUser, isClinician } = getUserPermissions(permissions)

  return isSupportUser || isClinician ? '' : OSANO_CMP_SCRIPT_SRC
}

/**
 * Client-side check: whether Osano should be active for the current user.
 * On SSR (no window), returns false — pass request-derived state via
 * OsanoCmpProvider for SSR/hydration-safe UI (e.g. CookiePreferencesLink).
 */
export function isOsanoCmpActiveForCurrentUser(): boolean {
  if (!isOsanoCmpEnabled) {
    return false
  }

  if (typeof window === 'undefined') {
    return false
  }

  const token =
    localStorage.getItem('access_token') ||
    // js-cookie is not required here; document.cookie fallback for parity
    getAccessTokenFromDocumentCookie()

  return Boolean(resolveOsanoCmpScriptSrc(token))
}

function getAccessTokenFromDocumentCookie(): string | null {
  if (typeof document === 'undefined') {
    return null
  }

  const prefix = `${COOKIE_KEYS.ACCESS_TOKEN}=`
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(prefix))

  return match ? decodeURIComponent(match.slice(prefix.length)) : null
}
